import { scaleLinear } from 'd3-scale';
import { curveMonotoneX, line } from 'd3-shape';
import type { Day } from '@map-my-money/engine';
import { money, monthYear } from '../../lib/format.ts';
import { useWidth } from '../../lib/hooks.ts';

/** A debt's balance from today to £0. */
export function PayoffChart({ path, label, height = 104 }: { path: readonly { date: Day; balance: number }[]; label: string; height?: number }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const first = path[0];
  const last = path[path.length - 1];
  if (!first || !last) return null;
  const pad = { l: 6, r: 6, t: 26, b: 8 };
  const x = scaleLinear().domain([first.date, last.date]).range([pad.l, Math.max(pad.l + 1, width - pad.r)]);
  const y = scaleLinear().domain([0, Math.max(1, first.balance)]).range([height - pad.b, pad.t]);
  const d = line<{ date: Day; balance: number }>().x((p) => x(p.date)).y((p) => y(p.balance)).curve(curveMonotoneX)(path) ?? '';
  const paidOff = last.balance <= 0.5;
  return (
    <div ref={ref} className="payoff-chart" style={{ height }}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={label}>
          <line x1={pad.l} x2={width - pad.r} y1={height - pad.b} y2={height - pad.b} className="chart__gridline" />
          <path d={d} fill="none" stroke="var(--terracotta)" strokeWidth={2.5} strokeLinecap="round" className="chart__reveal" />
          <circle cx={x(first.date)} cy={y(first.balance)} r={4} fill="var(--terracotta-text)" />
          <circle cx={x(last.date)} cy={y(last.balance)} r={4} fill="var(--slate)" />
          <text x={x(first.date)} y={y(first.balance) - 12} className="payoff-chart__start">{money(first.balance)}</text>
          {paidOff && (
            <text x={width - pad.r} y={pad.t - 12} textAnchor="end" className="payoff-chart__end">
              {money(0)} · {monthYear(last.date)}
            </text>
          )}
        </svg>
      )}
    </div>
  );
}
